import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { translate } from '../../../react-translator'; // Adjust path if necessary

/**
 * Higher-Order Component for providing a language-aware `t` function prop to class components.
 * @param {React.ComponentType} WrappedComponent - The component to wrap.
 * @returns {React.ComponentType} A new component with an injected `t` prop.
 */
const withTranslation = (WrappedComponent) => {
  const EnhancedComponent = (props) => {
    const { languageCode, ...restProps } = props;

    /**
     * Translates a message key using the current language.
     * @param {string | Object} message - The message key or message object.
     * @param {Object} [params={}] - Optional parameters for interpolation.
     * @param {boolean} [isNullableMessage=false] - Optional flag if the message can be null.
     * @returns {string} The translated string.
     */
    const t = (message, params = {}, isNullableMessage = false) =>
      translate(message, params, languageCode, isNullableMessage);

    return <WrappedComponent {...restProps} t={t} languageCode={languageCode} />;
  };

  EnhancedComponent.propTypes = {
    languageCode: PropTypes.string.isRequired,
  };

  const mapStateToProps = (state) => ({
    languageCode: state.language.currentLanguage.code,
  });

  // It's good practice to give a display name to the HOC for better debugging
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  EnhancedComponent.displayName = `withTranslation(${displayName})`;

  return connect(mapStateToProps)(EnhancedComponent);
};

export default withTranslation;
